import { Outlet } from '@tanstack/react-router'
import { type CSSProperties, Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useDispatch } from 'react-redux'
import App from '@/App'
import { Logger } from '@/components/Logger'
import { usePriceChangedSubscription } from '@/hooks/subscriptions/useAssetPricesSubscription'
import { useAssetsSubscription } from '@/hooks/subscriptions/useAssetsSubscription'
import { useUserBalanceSubscription } from '@/hooks/subscriptions/useUserBalanceSubscription'
import { useUserPnlSubscription } from '@/hooks/subscriptions/useUserPnlSubscription'
import { useListOpenOrderBooks } from '@/hooks/useListOpenOrderBooks'
import { useListAssets } from '@/hooks/v1'
import { ErrorFallBack } from '@/main'
import { SettingsProvider } from '@/providers/SettingsProvider'
import { ShortcutProvider } from '@/providers/ShortcutProvider'
import { toggleLayout } from '@/store/themeConfigSlice'
import { isBondexplorerVariant } from '@/utils/appVariant'
import Footer from './Footer'

const BOND_EXPLORER_LAYOUT_THEME = {
  '--color-black-trueBlack': '#030817',
  '--color-dark-darker': '#060D1C',
  '--color-panel': '#0A1428',
  '--color-panel-border': 'rgba(89, 216, 246, 0.18)',
  '--color-border': 'rgba(89, 216, 246, 0.16)',
  '--color-layer-1': '#030816',
  '--color-layer-2': '#071123',
  '--color-layer-3': '#0B1830',
  '--color-layer-4': '#10213D',
  '--color-layer-5': '#173354',
  '--color-layer-6': '#21446B',
  '--color-text-0': '#526887',
  '--color-text-1': '#8EA3C5',
  '--color-text-2': '#E5F6FF',
  '--color-warning': '#64D8F3',
  '--color-primary': '#64D8F3',
  '--color-success': '#72E3B0',
  '--color-danger': '#FF6F8F',
} as CSSProperties

function useSubscriptionGate(delayMs: number) {
  const [ready, setReady] = useState(delayMs <= 0)

  useEffect(() => {
    if (delayMs <= 0) {
      return
    }

    const timer = setTimeout(() => setReady(true), delayMs)
    return () => clearTimeout(timer)
  }, [delayMs])

  return ready
}

const ExplorerOnlyLayout = () => {
  const dispatch = useDispatch()

  const subscriptionsReady = useSubscriptionGate(2500)
  useUserBalanceSubscription({ enabled: subscriptionsReady })
  usePriceChangedSubscription({ enabled: subscriptionsReady })
  useUserPnlSubscription({ enabled: subscriptionsReady })
  useListAssets()
  useListOpenOrderBooks()
  useAssetsSubscription({ enabled: subscriptionsReady })

  useEffect(() => {
    dispatch(toggleLayout('full'))
  }, [dispatch])

  return (
    <App>
      <SettingsProvider>
        <div
          className="min-h-screen bg-black-trueBlack text-black dark:text-white-dark"
          style={isBondexplorerVariant ? BOND_EXPLORER_LAYOUT_THEME : undefined}
        >
          <Suspense>
            <ErrorBoundary
              onError={(error) => {
                Logger.error('Error in ExplorerOnlyLayout', error)
              }}
              FallbackComponent={ErrorFallBack}
            >
              <ShortcutProvider>
                <Outlet />
                <Footer isApiAvailable={true} />
              </ShortcutProvider>
            </ErrorBoundary>
          </Suspense>
        </div>
      </SettingsProvider>
    </App>
  )
}

export default ExplorerOnlyLayout
